import { Button, Col, Form, Input, Row, Select } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onGetAllCities } from 'modules/cities/services/cities';
import { CreateDistrictPayload, onCreateDistrict } from 'modules/districts/services/districts';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	active: 1;
	city_id: number;
};

function CreateDistrictPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [cities, setCities] = useState<{ label: string; value: number }[]>([]);
	const [createForm] = Form.useForm();

	useEffect(() => {
		getCities();
	}, []);

	const getCities = async () => {
		try {
			const response = await onGetAllCities();
			setCities(
				response.data.data.map((el: any) => ({
					label: el.name,
					value: el.id,
				})),
			);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};
	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createDistrictPayload: CreateDistrictPayload = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: 1,
				city_id: values.city_id,
			};

			await onCreateDistrict(createDistrictPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('DISTRICTS.CREATED_SUCCESS'));
			navigate('/districts');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('DISTRICTS.CREATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('DISTRICTS.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('DISTRICTS.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<h6>{t('DISTRICTS.INFORMATION')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('DISTRICTS.PLACEHOLDER.NAME_EN')} />
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('DISTRICTS.PLACEHOLDER.NAME_AR')} />
							</Form.Item>
						</Col>
					</Row>

					<h6>{t('DISTRICTS.CITY')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="city_id" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.CITY') }]}>
								<Select
									style={{ width: '100%' }}
									size="large"
									options={cities}
									loading={isLoading}
									showSearch
									filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateDistrictPage;
